import React, { ChangeEvent, useEffect, useRef, useState, KeyboardEvent, useCallback } from "react";
import { FieldArray, FieldArrayRenderProps, FormikProps } from "formik";
import TextField from "../../../../../shared/components/forms/TextField";
import Button from "../../../../../shared/components/general/Button";
import styled from "@emotion/styled";
import { css } from "@emotion/css";
import StyledDropdown from "../../../../../shared/scenes/applicationSettings/components/customAssistantConfigurations/StyledDropdown";
import { getAIAnsweringModuleId, getFoodOrderingModuleId, getHealthcareSchedulingModuleId } from "../../../../../shared/constants/ModuleConsts";
import { getAvailableTemplateConfigurations } from "../../../../../shared/api/controllers/templateConfigurations";
import { updateApplication } from "../../../../../shared/api/controllers/applications";
import { getAppliedApplicationTemplatesForApp } from "../../../../../shared/api/controllers/appliedApplicationTemplates";
import { AIAnsweringFeatureFlagId, FoodOrderingFeatureFlagId, HealthcareSchedulingFeatureFlagId, OutboundCallFeatureFlagId } from "../../../../../shared/constants/featureFlagIds";
import { getDefaultFormFieldsByAssistantType, getDefaultFormFieldsByPosName, getSandboxExtraFormFieldsByPosName } from "../../../../../shared/constants/sparkConstants/defaultFormFieldsByAssistantType";
import { SANDBOX_LOCATION_IDS } from "../../../../../shared/constants/sparkConstants/defaultSparkAssistantConfigurations";
import {
  color_shades_darker,
  color_shades_dark,
  color_shades_light,
  color_shades_lightest,
  silver_two,
  text_blue,
  dark_grey_blue,
  color_shades_darkest,
  color_colors_decline,
  color_variants_decline_light_1,
  pale_grey,
} from "../../../../../shared/constants/colors";
import CheckboxField from "../../../../../shared/components/forms/CheckboxField";
import ApplicationModel from "../../../../../shared/models/applications/api/ApplicationModel";
import TemplateFormSectionModel from "../../../../../shared/models/templating/api/TemplateFormSectionModel";
import TemplateFormFieldModel from "../../../../../shared/models/templating/api/TemplateFormFieldModel";
import OrganizationContainer from "../../../../../shared/state/containers/OrganizationContainer";
import ApplicationContainer from "../../../../../shared/state/containers/ApplicationContainer";
import CollapsingPanel from "../../../../../shared/components/structure/Panels/CollapsingPanel";
import ToggleIndicator from "../../../../../shared/components/general/ToggleIndicator";
import VerticalSeparator from "../../../../../shared/components/structure/VerticalSeparator";
import SearchField from "../../../../../shared/components/forms/SearchField";
import { breakpoint_small } from "../../../../../shared/constants/breakpoints";
import SecondaryLoader from "../../../../../shared/components/general/SecondaryLoader";
import useCSVHandler from "../../../../../shared/hooks/UseCSVHandler";
import { locationNameHeading, storeIdHeading, callDirectionHeading } from "../../../../../shared/constants/sparkConstants/formFieldImportHeadings";
import TooltipWrapper from "../../../../../shared/components/general/TooltipWrapper";
import PageError from "../../../../../shared/components/general/PageError";

const deleteIcon = require("../../../../../shared/content/images/custom-assistant-deployment/delete.svg");
const xIcon = require("../../../../../shared/content/images/x-small.svg");
const locationsIcon = require("../../../../../shared/content/images/spark/locations-dark.svg");
const checkIcon = require("../../../../../shared/content/images/dark-check.svg");
const searchIcon = require("../../../../../shared/content/images/search-blue.svg");
const pencilIcon = require("../../../../../shared/content/images/pencil-blue-small.svg");
const rightArrow = require("../../../../../shared/content/images/arrow-circle-right.svg");
const lightRightArrow = require("../../../../../shared/content/images/arrow-circle-right-light.svg");
const leftArrow = require("../../../../../shared/content/images/arrow-circle-left.svg");
const lightLeftArrow = require("../../../../../shared/content/images/arrow-circle-left-light.svg");

const fileDownloadIcon = require("../../../../../shared/content/images/menu-item-icons/file-code-download.svg");
const fileUploadIcon = require("../../../../../shared/content/images/menu-item-icons/file-code-upload.svg");
const alertCircleIcon = require("../../../../../shared/content/images/alert-circle.svg");

const WAIT_INTERVAL = 400;
const sparkOnboardingCueKey = 'SPARK_ONBOARDING_CUE';

export type LocationSubStep =
  | 'pending'
  | 'creating-app'
  | 'updating-app'
  | 'applying-template'
  | 'create-manage-menu'
  | 'pull-menu'
  | 'sync-module'
  | 'complete';

export interface ManageSparkLocationsFormProps {
  appContainer: ApplicationContainer;
  orgContainer: OrganizationContainer;
  commonApp: ApplicationModel;
  locationApps: ApplicationModel[];
  manageMenuApps?: ApplicationModel[];
  locationAppNames: string[];
  locationAppFormFields: TemplateFormFieldModel[];
  commonAppSections: TemplateFormSectionModel[];
  isLoadingTemplates: boolean;
  history: any;
  isSubmittingForm: boolean;
  setIsSubmittingForm: (isSubmittingForm: boolean) => void;
  progressStep?: string;
  setProgressStep?: (step: string) => void;
  progressLocationName?: string;
  setProgressLocationName?: (name: string) => void;
  progressMode?: 'creating' | 'updating';
  setProgressMode?: (mode: 'creating' | 'updating') => void;
  locationProgress?: Record<string, LocationSubStep>;
  setLocationProgress?: (updater: (prev: Record<string, LocationSubStep>) => Record<string, LocationSubStep>) => void;
  padding?: string;
}

export interface ManageSparkLocationsFormData {
  commonAppName?: string;
  locationNames: string[];
  selectedFormFields: TemplateFormFieldModel[];
  deselectedFormFields: TemplateFormFieldModel[];
  assistantType: string;
  assistantSetupType: string;     // 'standard' | 'out-of-the-box'
  pmsName?: string;
  // AI Ordering fields
  posNameOrdering?: string;       // POS Name for AI Ordering (separate from pmsName used for Dental Scheduling)
  storeIds?: Record<number, string>; // Per-location Store ID keyed by location index
  callDirections?: Record<number, string>; // Per-location Call Direction ('inbound' | 'outbound') for dental-scheduling
}

const SparkLocationsForm: React.FC<ManageSparkLocationsFormProps & FormikProps<ManageSparkLocationsFormData>> = (props) => {

  const [activeIndex, setActiveIndex] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLocationCollapsed, setIsLocationCollapsed] = useState(false);
  const [search, setSearch] = useState("");
  const [filteredSections, setFilteredSections] = useState(props.commonAppSections);
  const allSectionsRef = useRef<TemplateFormSectionModel[]>(props.commonAppSections);
  const [originalSelectedFormFields, setOriginalSelectedFormFields] = useState<TemplateFormFieldModel[]>();
  const hasPopulatedFormFieldsRef = useRef(false);
  const [isAssistantTypeDisabled, setIsAssistantTypeDisabled] = useState(false);
  const [submissionErrors, setSubmissionErrors] = useState<string[]>([]);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [showCheckboxes, setShowCheckboxes] = useState(true);

  // App name editing state
  const [editingAppId, setEditingAppId] = useState<string | null>(null);
  const [editingAppName, setEditingAppName] = useState<string>("");
  const [isSavingAppName, setIsSavingAppName] = useState(false);
  const [appNameSaveError, setAppNameSaveError] = useState<string | null>(null);
  // Local overrides for app names after a successful rename (appId -> new name)
  const [localAppNames, setLocalAppNames] = useState<Record<string, string>>({});

  // CSV import/export
  const { importedData, importCSV, exportCSV } = useCSVHandler();
  const csvImportInputRef = useRef<HTMLInputElement>(null);
  const locationTopicsRef = useRef<HTMLDivElement>(null);
  const posNameRef = useRef<HTMLDivElement>(null);
  const locationNamesRef = useRef<HTMLDivElement>(null);
  const stepperRef = useRef<HTMLDivElement>(null);

  const processLocationCSV = (csvArray: string[][]): { [key: string]: string }[] => {
    const isDental = props.values.assistantType === 'dental-scheduling';
    // Skip header row (index 0)
    return csvArray.slice(1)
      .filter(row => row.length >= 1 && row[0]?.trim())
      .map(row => {
        if (isDental) {
          // Dental scheduling columns: location name | call direction | default office id
          return {
            [locationNameHeading]: row[0]?.trim() ?? '',
            [callDirectionHeading]: row[1]?.trim() ?? '',
            [storeIdHeading]: row[2]?.trim() ?? '',
          };
        }
        // AI Ordering / AI Answering columns: location name | store id
        return {
          [locationNameHeading]: row[0]?.trim() ?? '',
          [storeIdHeading]: row[1]?.trim() ?? '',
        };
      });
  };

  const handleCSVImport = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      importCSV(file, processLocationCSV);
    }
    // Reset the input so the same file can be re-imported if needed
    e.target.value = '';
  };

  const downloadLocationTemplate = () => {
    if (props.values.assistantType === 'dental-scheduling') {
      exportCSV(
        [{ [locationNameHeading]: '', [callDirectionHeading]: '', [storeIdHeading]: '' }],
        [locationNameHeading, callDirectionHeading, storeIdHeading],
        'location-import-template'
      );
    } else {
      exportCSV(
        [{ [locationNameHeading]: '', [storeIdHeading]: '' }],
        [locationNameHeading, storeIdHeading],
        'location-import-template'
      );
    }
  };

  const searchTimer = useRef<NodeJS.Timer>(null);

  const assistantTypeOptions = [
    { value: 'ai-answering', label: 'AI Answering' },
    { value: 'ai-ordering', label: 'AI Ordering' },
    { value: 'dental-scheduling', label: 'Dental Scheduling' }
  ];

  const pmsNameOptions = [
    { value: 'cloud-9', label: 'Cloud 9' },
    { value: 'denticon', label: 'Denticon' },
    { value: 'dentrix-ascend', label: 'Dentrix Ascend' }
  ];

  // POS Name options for AI Ordering — values must match the POS name constants used by the ordering backend
  const posNameOrderingOptions = [
    { value: 'ADORA', label: 'Adora' },
    { value: 'OLO', label: 'Olo' },
    { value: 'PAYTRONIX', label: 'Paytronix' },
    { value: 'SPEEDLINE', label: 'Speedline' },
    { value: 'TOAST', label: 'Toast' },
  ];

  const assistantSetupTypeOptions = [
    { value: 'standard', label: 'Production' },
    { value: 'out-of-the-box', label: 'Voicify Sandbox' },
  ];

  const callDirectionOptions = [
    { value: 'inbound', label: 'Inbound' },
    { value: 'outbound', label: 'Outbound' },
    { value: 'inbound-outbound', label: 'Inbound & Outbound' },
  ];

  const subsequentVisitCallDirectionOptions = [
    { value: 'inbound', label: 'Inbound' },
    { value: 'outbound', label: 'Outbound' },
    { value: 'inbound-outbound', label: 'Inbound & Outbound' },
  ];

  // Function to determine assistant type from existing apps
  const determineAssistantTypeFromApps = () => {
    // If there's a common app present, disable the dropdown
    if (props.commonApp) {
      setIsAssistantTypeDisabled(true);

      // Check the common app for feature flags to determine type
      if (props.commonApp.featureFlags) {
        const featureFlags = props.commonApp.featureFlags;

        if (featureFlags.some(flag => flag.id === HealthcareSchedulingFeatureFlagId)) {
          return 'dental-scheduling';
        } else if (featureFlags.some(flag => flag.id === FoodOrderingFeatureFlagId)) {
          return 'ai-ordering';
        } else if (featureFlags.some(flag => flag.id === AIAnsweringFeatureFlagId)) {
          return 'ai-answering';
        }
      }

      // Common app exists but no matching feature flags found
      return null;
    }

    // No common app, dropdown should be enabled
    setIsAssistantTypeDisabled(false);
    return null;
  };

  // Function to get sections based on selected assistant type
  const getSectionsForAssistantType = async (assistantType: string) => {
    if (!assistantType) return [];

    try {
      const availableTemplateConfigsResponse = await getAvailableTemplateConfigurations(props.orgContainer.state.currentOrganization.id);

      let selectedModuleId: string;
      switch (assistantType) {
        case 'ai-answering':
          selectedModuleId = getAIAnsweringModuleId();
          break;
        case 'ai-ordering':
          selectedModuleId = getFoodOrderingModuleId();
          break;
        case 'dental-scheduling':
          selectedModuleId = getHealthcareSchedulingModuleId();
          break;
        default:
          selectedModuleId = getAIAnsweringModuleId();
      }

      const selectedModuleConfig = availableTemplateConfigsResponse.data.find(config => config.id === selectedModuleId);
      const sections = selectedModuleConfig?.templateForm?.templateFormSections || [];

      return sections;
    } catch (error) {
      console.error('Error loading sections for assistant type:', error);
      return [];
    }
  };

  useEffect(() => {
    setOriginalSelectedFormFields(props.values.selectedFormFields);
  }, [])

  // Effect to determine assistant type based on existing apps
  useEffect(() => {
    const determinedType = determineAssistantTypeFromApps();
    if (determinedType && !props.values.assistantType) {
      props.setFieldValue("assistantType", determinedType);
    }
  }, [props.locationApps, props.commonApp])

  // Update filtered sections when assistant type changes
  useEffect(() => {
    const updateSectionsForAssistantType = async () => {
      if (props.values.assistantType) {
        const newSections = await getSectionsForAssistantType(props.values.assistantType);
        allSectionsRef.current = newSections;
        setFilteredSections(newSections);

        // Apply default form fields for the selected assistant type if:
        // 1. There are no existing location app form fields (new assistant being created)
        // 2. The selected form fields are currently empty
        const shouldApplyDefaults = props.locationAppFormFields.length === 0 && props.values.selectedFormFields.length === 0 && !props.commonApp;

        if (shouldApplyDefaults) {
          const defaultFieldTitles = getDefaultFormFieldsByAssistantType(props.values.assistantType);

          // Find matching form fields from the sections
          const allFormFields = newSections.flatMap(section => section.templateFormFields);
          const defaultFormFields = allFormFields.filter(field =>
            (field.associatedVariable && defaultFieldTitles.includes(field.associatedVariable))
            || defaultFieldTitles.includes(field.title)
          );

          if (defaultFormFields.length > 0) {
            props.setFieldValue("selectedFormFields", defaultFormFields);
          }
        }

        setActiveIndex(0);
      } else {
        setFilteredSections([]);
      }
    };

    updateSectionsForAssistantType();
  }, [props.values.assistantType]);

  // When the POS name changes for AI Ordering, apply POS-specific default form fields
  useEffect(() => {
    if (props.values.assistantType !== 'ai-ordering' || !props.values.posNameOrdering) return;

    const shouldApplyDefaults = props.locationAppFormFields.length === 0
      && props.values.selectedFormFields.length === 0
      && !props.commonApp;

    if (shouldApplyDefaults) {
      const defaultFieldTitles = getDefaultFormFieldsByPosName(props.values.posNameOrdering);
      if (defaultFieldTitles.length === 0) return;

      const allFormFields = filteredSections.flatMap(section => section.templateFormFields);
      const defaultFormFields = allFormFields.filter(field =>
        (field.associatedVariable && defaultFieldTitles.includes(field.associatedVariable))
        || defaultFieldTitles.includes(field.title)
      );
      if (defaultFormFields.length > 0) {
        props.setFieldValue("selectedFormFields", defaultFormFields);
      }
    }
  }, [props.values.posNameOrdering]);

  // When Environment or PMS Name changes for Dental Scheduling, pre-populate sandbox Location IDs for all location rows.
  // Always overwrite existing storeIds when pmsName changes in sandbox mode because each PMS uses a different sandbox Location ID.
  useEffect(() => {
    if (props.values.assistantSetupType !== 'out-of-the-box') return;
    if (props.values.assistantType !== 'dental-scheduling') return;
    if (!props.values.pmsName) return;

    const dentalSandboxLocationIds: Record<string, string> = {
      'denticon': '266',
      'dentrix-ascend': '7000000000392',
    };
    const sandboxLocationId = dentalSandboxLocationIds[props.values.pmsName];
    const updatedStoreIds: Record<number, string> = {};
    props.values.locationNames.forEach((_, index) => {
      // Always overwrite — sandbox Location IDs differ per PMS, so a stale value from
      // a previous PMS selection would be wrong.
      updatedStoreIds[index] = sandboxLocationId ?? '';
    });
    props.setFieldValue('storeIds', updatedStoreIds);
  }, [props.values.assistantSetupType, props.values.pmsName]);

  // When Environment or POS name changes, pre-populate sandbox Location IDs for all location rows
  // and check any sandbox-only extra fields (e.g. Special Dates & Holidays, Time Zone).
  // Always overwrite existing storeIds when posNameOrdering changes in sandbox mode because each
  // System Integration uses a different sandbox Location ID (OLO/ADORA have none — wipe to blank).
  useEffect(() => {
    if (props.values.assistantSetupType !== 'out-of-the-box') return;
    if (props.values.assistantType !== 'ai-ordering') return;
    if (!props.values.posNameOrdering) return;

    const sandboxLocationId = SANDBOX_LOCATION_IDS[props.values.posNameOrdering];
    const updatedStoreIds: Record<number, string> = {};
    props.values.locationNames.forEach((_, index) => {
      // Always overwrite — sandbox Location IDs differ per integration, so a stale value from
      // a previous selection would be wrong. Leave blank for integrations with no sandbox ID (OLO, ADORA).
      updatedStoreIds[index] = sandboxLocationId ?? '';
    });
    props.setFieldValue('storeIds', updatedStoreIds);

    // Add sandbox-specific extra fields to selectedFormFields if not already selected
    const extraFieldTitles = getSandboxExtraFormFieldsByPosName(props.values.posNameOrdering);
    if (extraFieldTitles.length > 0) {
      const allFormFields = filteredSections.flatMap(section => section.templateFormFields);
      const extraFields = allFormFields.filter(
        field => {
          const matches =
            (field.associatedVariable && extraFieldTitles.includes(field.associatedVariable))
            || extraFieldTitles.includes(field.title);
          return matches && !props.values.selectedFormFields.some(sf => sf.id === field.id);
        }
      );
      if (extraFields.length > 0) {
        props.setFieldValue('selectedFormFields', [...props.values.selectedFormFields, ...extraFields]);
      }
    }
  }, [props.values.assistantSetupType, props.values.posNameOrdering]);

  useEffect(() => {
    // set initial selectedFormFields value, since it's not set in time by the mapPropsToValues Formik function
    if (props.commonApp) {
      props.setFieldValue("commonAppName", props.commonApp?.name);
    }
    if (props.locationAppFormFields.length > 0 && !hasPopulatedFormFieldsRef.current) {
      const locationAppFormFieldsCopy = [...props.locationAppFormFields];
      for (const locationAppFormField of locationAppFormFieldsCopy) {
        const commonAppFormField = props.commonAppSections
          .flatMap((section) => section.templateFormFields)
          .find((formField) => formField.title === locationAppFormField.title);
        if (commonAppFormField) {
          locationAppFormField.id = commonAppFormField.id;
        }
      }
      props.setFieldValue("selectedFormFields", locationAppFormFieldsCopy);
      hasPopulatedFormFieldsRef.current = true;
    }
    if (props.locationAppNames.length > 0) {
      props.setFieldValue("locationNames", props.locationAppNames);
    } else {
      props.setFieldValue("locationNames", [""]);
    }
  }, [props.locationAppFormFields, props.locationAppNames, props.commonApp?.name]);

  useEffect(() => {
    // Populate PMS/POS name and per-location store IDs from the existing common/location apps
    if (!props.commonApp) return;

    // Derive assistant type from feature flags first (authoritative), fall back to Formik value.
    // This avoids a race condition where props.values.assistantType hasn't been committed to
    // Formik state yet when this effect first fires.
    const derivedAssistantType = (() => {
      if (props.commonApp.featureFlags?.some(f => f.id === HealthcareSchedulingFeatureFlagId)) return 'dental-scheduling';
      if (props.commonApp.featureFlags?.some(f => f.id === FoodOrderingFeatureFlagId)) return 'ai-ordering';
      return null;
    })() || props.values.assistantType;

    const populateFromExistingApp = async () => {
      // Prefer the app-level pmsName/posName properties. If not present, fall back to reading
      // the saved value from the shared configuration form fields ({PMSName} / {Webhook-Pos-Name}).
      if (props.commonApp?.id) {
        if (derivedAssistantType === 'dental-scheduling') {
          if (props.commonApp.pmsName) {
            props.setFieldValue("pmsName", props.commonApp.pmsName);
          } else {
            const appliedTemplatesResponse = await getAppliedApplicationTemplatesForApp(props.commonApp.id);
            const allFieldValues = appliedTemplatesResponse?.data?.flatMap(
              at => at.templateFormFieldValues ?? []
            ) ?? [];
            const pmsFieldValue = allFieldValues.find(fv => fv.associatedVariable === '{PMSName}')?.value;
            if (pmsFieldValue) {
              // {PMSName} stores the human-readable label (e.g. "Denticon"). Normalize to the
              // option value slug (e.g. "denticon") so the dropdown renders the correct selection.
              const matchedOption = pmsNameOptions.find(
                o => o.label.toLowerCase() === pmsFieldValue.toLowerCase() || o.value.toLowerCase() === pmsFieldValue.toLowerCase()
              );
              props.setFieldValue("pmsName", matchedOption ? matchedOption.value : pmsFieldValue);
            }
          }
        } else if (derivedAssistantType === 'ai-ordering') {
          const firstLocationApp = props.locationApps?.[0];
          const existingPosName = props.commonApp.posName ?? firstLocationApp?.posName;
          if (existingPosName) {
            props.setFieldValue("posNameOrdering", existingPosName);
          } else {
            const appliedTemplatesResponse = await getAppliedApplicationTemplatesForApp(props.commonApp.id);
            const allFieldValues = appliedTemplatesResponse?.data?.flatMap(
              at => at.templateFormFieldValues ?? []
            ) ?? [];
            const posFieldValue = allFieldValues.find(fv => fv.associatedVariable === '{Webhook-Pos-Name}')?.value;
            if (posFieldValue) {
              // {Webhook-Pos-Name} stores the POS name constant (e.g. "OLO", "SPEEDLINE").
              // Normalize to the option value so the dropdown renders the correct selection.
              const matchedOption = posNameOrderingOptions.find(
                o => o.label.toLowerCase() === posFieldValue.toLowerCase() || o.value.toLowerCase() === posFieldValue.toLowerCase()
              );
              props.setFieldValue("posNameOrdering", matchedOption ? matchedOption.value : posFieldValue);
            }
          }
        }
      }

      if (props.locationApps?.length > 0) {
        const newStoreIds: Record<number, string> = {};
        await Promise.all(props.locationApps.map(async (locationApp, index) => {
          const storeId = derivedAssistantType === 'dental-scheduling'
            ? locationApp.pmsLocationId
            : locationApp.posLocationId;
          if (storeId) {
            newStoreIds[index] = storeId;
          } else if (locationApp.id) {
            // App-level ID not populated — read from the location app's applied template form fields.
            // Healthcare uses {defaultOfficeId}, AI Ordering uses {Webhook-Restaurant-Id}.
            const associatedVariable = derivedAssistantType === 'dental-scheduling'
              ? '{defaultOfficeId}'
              : '{Webhook-Restaurant-Id}';
            try {
              const locationAppliedTemplates = await getAppliedApplicationTemplatesForApp(locationApp.id);
              const locationFieldValues = locationAppliedTemplates?.data?.flatMap(
                at => at.templateFormFieldValues ?? []
              ) ?? [];
              const fieldValue = locationFieldValues.find(fv => fv.associatedVariable === associatedVariable)?.value;
              if (fieldValue) {
                newStoreIds[index] = fieldValue;
              }
            } catch (error) {
              console.error(
                "Failed to load applied template field values for Spark location prepopulation.",
                {
                  locationAppId: locationApp.id,
                  associatedVariable,
                  error,
                }
              );
            }
          }
        }));
        props.setFieldValue('storeIds', newStoreIds);
      }

      const newCallDirections: Record<number, string> = {};
      props.locationApps.forEach((locationApp, index) => {
        const locationHasOutbound = locationApp.featureFlags?.some((f: any) => f.id === OutboundCallFeatureFlagId)
          ?? props.commonApp.featureFlags?.some(f => f.id === OutboundCallFeatureFlagId);
        newCallDirections[index] = locationHasOutbound ? 'outbound' : 'inbound';
      });
      props.setFieldValue('callDirections', newCallDirections);
    };

    populateFromExistingApp().catch(error =>
      console.error("Failed to populate from existing app for Spark location prepopulation.", error)
    );
  }, [props.commonApp?.id, props.locationApps, props.values.assistantType]);

  useEffect(() => {
    const deselectedFormFields = originalSelectedFormFields?.filter((originalFormField) => {
      return !props.values.selectedFormFields.some((selectedFormField) => selectedFormField.title === originalFormField.title);
    });
    props.setFieldValue("deselectedFormFields", deselectedFormFields);
  }, [props.values.selectedFormFields]);

  // Apply CSV-imported location data into Formik whenever importedData changes
  useEffect(() => {
    if (!importedData || importedData.length === 0) return;

    const newLocationNames: string[] = importedData.map((row: any) => row[locationNameHeading] ?? '');
    const newStoreIds: Record<number, string> = {};
    importedData.forEach((row: any, index: number) => {
      newStoreIds[index] = row[storeIdHeading] ?? '';
    });

    props.setFieldValue('locationNames', newLocationNames);
    props.setFieldValue('storeIds', newStoreIds);

    if (props.values.assistantType === 'dental-scheduling' && !props.commonApp) {
      const newCallDirections: Record<number, string> = {};
      importedData.forEach((row: any, index: number) => {
        const val = row[callDirectionHeading]?.trim() ?? '';
        if (val) {
          newCallDirections[index] = val;
        }
      });
      props.setFieldValue('callDirections', newCallDirections);
    }
  }, [importedData]);

  // Effect to handle submission results from props or form validation
  useEffect(() => {
    // Check if there are any Formik errors that should be displayed
    if (isSubmitted && !props.isSubmittingForm) {
      // Check for status results (from handleSubmit)
      if (props.status?.type === 'error') {
        const statusErrors = [props.status.message];
        if (props.status.details && props.status.details.length > 0) {
          statusErrors.push(...props.status.details);
        }
        handleFormSubmissionResult(false, statusErrors);
        return;
      }

      if (props.status?.type === 'success') {
        handleFormSubmissionResult(true);
        return;
      }

      // Check for field validation errors
      const formErrors = Object.keys(props.errors).length > 0 ? Object.values(props.errors) : [];
      if (formErrors.length > 0) {
        handleFormSubmissionResult(false, formErrors as string[]);
      }
      // Don't auto-set success anymore - wait for explicit success status
    }
  }, [isSubmitted, props.isSubmittingForm, props.errors, props.status]);

  // Auto-scroll to the stepper when submission begins
  useEffect(() => {
    if (props.isSubmittingForm) {
      setTimeout(() => {
        scrollToWithOffset(stepperRef.current);
      }, 50);
    }
  }, [props.isSubmittingForm]);


  const createFilteredSections = (search: string) => {
    if (search?.length) {
      const searchLower = search.toLowerCase();
      const searchFilteredSections = allSectionsRef.current
        .map((section) => {
          const sectionMatches = section.title.toLowerCase().includes(searchLower);
          const filteredFields = sectionMatches
            ? section.templateFormFields
            : section.templateFormFields.filter(
                (formField) => formField.title.toLowerCase().includes(searchLower)
              );
          return { ...section, templateFormFields: filteredFields };
        })
        .filter((section) => section.templateFormFields.length > 0);

      setFilteredSections(searchFilteredSections);
    } else {
      setFilteredSections(allSectionsRef.current);
    }
  };

  const handleTextFieldChange = (e: ChangeEvent<HTMLInputElement>, fieldName: string) => {
    return props.setFieldValue(fieldName, e.target.value);
  };

  const handleAssistantTypeChange = (selectedOption: any, value: string) => {
    // If user manually changes assistant type and there are no existing locationAppFormFields,
    // we can clear the selected form fields to start fresh
    const shouldClearSelections = props.locationAppFormFields.length === 0 && props.values.selectedFormFields.length > 0;

    props.setFieldValue("assistantType", value);

    if (shouldClearSelections) {
      props.setFieldValue("selectedFormFields", []);
    }

    // Clear PMS Name if changing away from dental scheduling
    if (value !== 'dental-scheduling') {
      props.setFieldValue("pmsName", undefined);
    }

    // Clear AI Ordering fields if changing away from ai-ordering
    if (value !== 'ai-ordering') {
      props.setFieldValue("posNameOrdering", undefined);
      props.setFieldValue("storeIds", {});
    }

    setTimeout(() => {
      scrollToWithOffset(posNameRef.current);
    }, 50);
  };

  const SCROLL_OFFSET = 72; // 56px tab bar + 16px breathing room

  const scrollToWithOffset = (el: HTMLElement | null) => {
    if (!el) return;
    // Walk up to find the nearest scrollable ancestor
    let parent = el.parentElement;
    while (parent) {
      const overflow = window.getComputedStyle(parent).overflowY;
      if (overflow === 'auto' || overflow === 'scroll') break;
      parent = parent.parentElement;
    }
    const container = parent ?? window.document.documentElement;
    const elTop = el.getBoundingClientRect().top;
    const containerTop = container instanceof HTMLElement ? container.getBoundingClientRect().top : 0;
    const currentScroll = container instanceof HTMLElement ? container.scrollTop : window.scrollY;
    container.scrollTo({ top: currentScroll + elTop - containerTop - SCROLL_OFFSET, behavior: 'smooth' });
  };

  const handlePmsNameChange = (selectedOption: any, value: string) => {
    props.setFieldValue("pmsName", value);
  };

  const handlePosNameOrderingChange = (selectedOption: any, value: string) => {
    props.setFieldValue("posNameOrdering", value);
  };

  const handleAssistantSetupTypeChange = (selectedOption: any, value: string) => {
    props.setFieldValue("assistantSetupType", value);
    // When switching to Production, clear sandbox-populated store IDs so they don't carry over.
    // When switching to Sandbox, the useEffect hooks will re-populate store IDs based on the
    // selected PMS/POS name.
    if (value === 'standard') {
      props.setFieldValue("storeIds", {});
    }
    setTimeout(() => {
      scrollToWithOffset(locationNamesRef.current);
    }, 50);
  };

  const handleCheckboxChange = (e: ChangeEvent<HTMLInputElement>, commonAppFormField: TemplateFormFieldModel) => {
    const selectedFormFieldsCopy = [...props.values.selectedFormFields];
    if (e.target.checked) {
      // add form field to selected form fields if not already there
      if (
        selectedFormFieldsCopy.some(
          (selectedFormField) => selectedFormField.id === commonAppFormField.id
        )
      ) {
        // do nothing if already checked
        return;
      } else {
        // Look in both filtered sections and original common app sections to handle all cases
        const filteredFormFields = filteredSections.flatMap(
          (section) => section.templateFormFields
        );
        const commonAppFormFields = props.commonAppSections.flatMap(
          (section) => section.templateFormFields
        );

        // Try to find the field in filtered sections first, then fall back to common app sections
        let selectedFormFieldToCheck = filteredFormFields.find(
          (formField) => formField.id === commonAppFormField.id || formField.title === commonAppFormField.title
        );

        if (!selectedFormFieldToCheck) {
          selectedFormFieldToCheck = commonAppFormFields.find(
            (formField) => formField.id === commonAppFormField.id || formField.title === commonAppFormField.title
          );
        }

        if (selectedFormFieldToCheck) {
          selectedFormFieldsCopy.push(selectedFormFieldToCheck);
        }
      }
    } else {
      // remove form field from selected form fields if it is there
      const selectedFormFieldToUncheck = selectedFormFieldsCopy.find(
        (selectedFormField) => selectedFormField.id === commonAppFormField.id || selectedFormField.title === commonAppFormField.title
      );

      if (selectedFormFieldToUncheck) {
        selectedFormFieldsCopy.splice(
          selectedFormFieldsCopy.indexOf(selectedFormFieldToUncheck),
          1
        );
      }
    }
    props.setFieldValue("selectedFormFields", selectedFormFieldsCopy);
  };

  const renderSectionFormFields = (commonAppSection: TemplateFormSectionModel) => {
    if (commonAppSection.templateFormFields.length > 0) {
      return commonAppSection?.templateFormFields?.map((commonAppFormField) => {
        return (
          <CheckboxField
            label={commonAppFormField.title}
            key={commonAppFormField.id}
            name={commonAppFormField.title}
            checked={props.values.selectedFormFields.some(
              (selectedFormField) =>
                selectedFormField.title === commonAppFormField.title ||
                selectedFormField.id === commonAppFormField.id
            )} //checking both titles and ids for flexibility
            onChange={(e) => handleCheckboxChange(e, commonAppFormField)}
            className={checkboxField}
            disabled={props.isLoadingTemplates}
            tooltip={commonAppFormField?.tip}
            tooltipPosition={"right"}
            tooltipStyle={tooltipStyle}
            tooltipContentStyle={tooltipContentStyle}
          />
        );
      });
    }
  };

  const handleNextButtonClick = () => {
    if (activeIndex < filteredSections.length - 1) {
      setActiveIndex(activeIndex + 1);
    }
  };

  const handlePreviousButtonClick = () => {
    if (activeIndex <= filteredSections.length - 1) {
      setActiveIndex(activeIndex - 1);
    }
  };

  const handleSectionDropdownChange = (_fieldName: string, value: string) => {
    setActiveIndex(parseInt(value, 10));
  };

  const handleSubmitButtonClick = () => {
    // Clear previous errors and success status
    setSubmissionErrors([]);
    setSubmissionSuccess(false);
    setIsSubmitted(true);
    // Mark all fields touched so Formik inline errors become visible
    const touchedLocationNames = props.values.locationNames.reduce((acc, _, i) => ({ ...acc, [i]: true }), {});
    const touchedStoreIds = Object.keys(props.values.storeIds ?? {}).reduce((acc, k) => ({ ...acc, [k]: true }), {});
    props.setTouched({
      assistantType: true,
      assistantSetupType: true,
      pmsName: true,
      posNameOrdering: true,
      locationNames: touchedLocationNames as any,
      storeIds: touchedStoreIds as any,
    });
  };

  const handleFormSubmissionResult = (success: boolean, errors: string[] = []) => {
    if (success) {
      // Only set the onboarding cue on initial creation (Submit All), not on subsequent updates.
      // isInitialConfiguration mirrors the condition used to show the "Submit All" button label.
      const isInitialConfiguration = !props.commonApp && (!props.locationApps || props.locationApps.length === 0);
      if (isInitialConfiguration) {
        localStorage.setItem(sparkOnboardingCueKey, JSON.stringify({
          organizationId: props.orgContainer.state.currentOrganization.id,
          stage: 'shared-config'
        }));
      }
      setSubmissionSuccess(true);
      setSubmissionErrors([]);
      setTimeout(() => {
        props.history.push("/");
      }, 1500);
    } else {
      setSubmissionSuccess(false);
      setIsSubmitted(false);
      setSubmissionErrors(errors.length > 0 ? errors : ["An unexpected error occurred during form submission."]);
    }
  };

  const handleSearch = (searchEvent: React.ChangeEvent<HTMLInputElement>) => {
    clearTimeout(searchTimer.current);
    setSearch(searchEvent.target.value);
    searchTimer.current = setTimeout(() => {
      createFilteredSections(searchEvent.target.value);
      setActiveIndex(0);
    }, WAIT_INTERVAL);
  };

  const clearSearchTerm = () => {
    setSearch("");
    setActiveIndex(0);
    setFilteredSections(allSectionsRef.current);
  };

  const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      clearSearchTerm();
    }
  };

  const handleStartEditAppName = (appId: string, currentName: string) => {
    setEditingAppId(appId);
    setEditingAppName(currentName);
    setAppNameSaveError(null);
  };

  const handleCancelEditAppName = () => {
    setEditingAppId(null);
    setEditingAppName("");
    setAppNameSaveError(null);
  };

  const handleSaveAppName = async (app: ApplicationModel) => {
    const trimmed = editingAppName.trim();
    if (!trimmed) {
      setAppNameSaveError("App name cannot be empty.");
      return;
    }
    if (trimmed === app.name) {
      handleCancelEditAppName();
      return;
    }
    setIsSavingAppName(true);
    setAppNameSaveError(null);
    try {
      const result = await updateApplication(app.id, {
        name: trimmed,
        shortDescription: app.shortDescription,
        description: app.description,
        keywords: app.keywords,
        invocationPhrase: app.invocationPhrase,
        imageItemId: app.imageItemId,
        featureFlags: app.featureFlags,
        posName: app.posName,
        pmsName: app.pmsName,
        posLocationId: app.posLocationId,
        pmsLocationId: app.pmsLocationId,
        isLive: app.isLive,
      });
      if (result.resultType === "Ok") {
        setLocalAppNames(prev => ({ ...prev, [app.id]: trimmed }));
        // If this is a location app, update the Formik locationNames so the row reflects the new name
        const locationIndex = props.values.locationNames.indexOf(app.name);
        if (locationIndex !== -1) {
          const updatedNames = [...props.values.locationNames];
          updatedNames[locationIndex] = trimmed;
          props.setFieldValue("locationNames", updatedNames);
        }
        setEditingAppId(null);
        setEditingAppName("");
      } else {
        setAppNameSaveError(result.errors?.join(", ") || "Failed to update app name.");
      }
    } catch (e) {
      setAppNameSaveError("An unexpected error occurred.");
    } finally {
      setIsSavingAppName(false);
    }
  };

  const handleAppNameEditKeyDown = (e: KeyboardEvent<HTMLInputElement>, app: ApplicationModel) => {
    if (e.key === "Enter") {
      handleSaveAppName(app);
    } else if (e.key === "Escape") {
      handleCancelEditAppName();
    }
  };

  const renderLocationButtons = (index: number) => {
    const sectionOptions = filteredSections.map((section, i) => ({
      label: section.title,
      value: String(i),
    }));

    return (
      <SectionHeaderRow>
        {filteredSections.length >= 1 && (
          <SectionDropdownWrapper>
            <StyledDropdown
              fieldName="sectionNav"
              options={sectionOptions}
              value={String(index)}
              handleOnChange={handleSectionDropdownChange}
              placeholder="Jump to section..."
              searchable={true}
              openMenuAbove={false}
              size="medium"
              disabled={!!search}
            />
          </SectionDropdownWrapper>
        )}

        <SearchButtonsWrapper>
          <StyledSearchField
              placeholder={"Search All Form Fields"}
              boxType={"rounded"}
              onChange={handleSearch}
              onKeyDown={handleSearchKeyDown}
              searchIcon={searchIcon}
              value={search}
              deletePosition={"right"}
              deleteIcon={xIcon}
              handleClear={clearSearchTerm}
            />

          {index !== filteredSections.length - 1 ? (
            <ButtonWrapper>
              <StyledButton
                className={index === 0 && lightButtonStyle}
                onClick={(e) => {
                  e.stopPropagation();
                  (index !== 0 ? handlePreviousButtonClick() : e.stopPropagation())
                }
                }
                themes={["icon"]}
                icon={index !== 0 ? leftArrow : lightLeftArrow}
                disabled={index === 0}
              />
              <StyledButton
                onClick={(e) => { handleNextButtonClick(); e.stopPropagation(); }}
                themes={["icon"]}
                icon={filteredSections.length > 0 ? rightArrow : lightRightArrow}
                disabled={filteredSections.length === 0}
              />
            </ButtonWrapper>
          ) : (
            <ButtonWrapper>
              <StyledButton
                className={filteredSections.length === 1 && lightButtonStyle}
                onClick={(e) => {
                  filteredSections.length !== 1
                    ? handlePreviousButtonClick()
                    : null
                  e.stopPropagation();
                }}
                themes={["icon"]}
                icon={filteredSections.length !== 1 ? leftArrow : lightLeftArrow}
                disabled={filteredSections.length === 1}
              />
              <StyledButton
                className={lightButtonStyle}
                onClick={(e) => e.stopPropagation()}
                disabled={true}
                themes={["icon"]}
                icon={lightRightArrow}
              />
            </ButtonWrapper>
          )}
        </SearchButtonsWrapper>
      </SectionHeaderRow>
    );
  };

  const renderSubmitButton = () => {
    // If form is submitted and successful, show Go to Dashboard button
    if (isSubmitted && submissionSuccess) {
      return (
        <SubmitButtonWrapper>
          <StyledButton
            onClick={() => props.history.push("/")}
            themes={["primary"]}
            text="Go to Dashboard"
            type="button"
          />
        </SubmitButtonWrapper>
      );
    }

    // Default state: show Submit All / Update All button
    return (
      <SubmitButtonWrapper>
        {submissionErrors.length > 0 && (
          <PageError errors={submissionErrors} onClear={() => setSubmissionErrors([])} />
        )}
        <StyledButton
          onClick={async () => {
            handleSubmitButtonClick();
            const errors = await props.validateForm();
            if (Object.keys(errors).length === 0) {
              props.handleSubmit();
            } else {
              requestAnimationFrame(() => {
                const firstError = document.querySelector('[data-validation-error]');
                scrollToWithOffset(firstError as HTMLElement | null);
              });
            }
          }}
          loading={props.isSubmittingForm}
          themes={["primary"]}
          text={(!props.commonApp && (!props.locationApps || props.locationApps.length === 0)) ? "Submit All" : "Update All"}
          type="button"
        />
      </SubmitButtonWrapper>
    );
  };

  const handleAddLocationButtonClick = (arrayHelpers: FieldArrayRenderProps) => {
    arrayHelpers.push("");
    isLocationCollapsed && setIsLocationCollapsed(!isLocationCollapsed);

    // If we're in a post-success state, re-enable the form for the new location
    if (submissionSuccess) {
      setIsSubmitted(false);
      setShowCheckboxes(true);
    }

    // Pre-populate the new row's Location ID with the sandbox value if applicable
    if (
      props.values.assistantSetupType === 'out-of-the-box' &&
      props.values.assistantType === 'ai-ordering' &&
      props.values.posNameOrdering
    ) {
      const sandboxLocationId = SANDBOX_LOCATION_IDS[props.values.posNameOrdering];
      if (sandboxLocationId) {
        const newIndex = props.values.locationNames.length;
        const currentStoreIds = props.values.storeIds ?? {};
        if (!currentStoreIds[newIndex]) {
          props.setFieldValue('storeIds', { ...currentStoreIds, [newIndex]: sandboxLocationId });
        }
      }
    }

    // Pre-populate the new row's Location ID for Dental Scheduling with Voicify Sandbox
    if (
      props.values.assistantSetupType === 'out-of-the-box' &&
      props.values.assistantType === 'dental-scheduling' &&
      props.values.pmsName
    ) {
      const dentalSandboxLocationIds: Record<string, string> = {
        'denticon': '266',
        'dentrix-ascend': '7000000000392',
      };
      const sandboxLocationId = dentalSandboxLocationIds[props.values.pmsName];
      if (sandboxLocationId) {
        const newIndex = props.values.locationNames.length;
        const currentStoreIds = props.values.storeIds ?? {};
        if (!currentStoreIds[newIndex]) {
          props.setFieldValue('storeIds', { ...currentStoreIds, [newIndex]: sandboxLocationId });
        }
      }
    }
  };

  const handleAddLocationKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.currentTarget.blur();
    }
  };

  const renderAddButton = (arrayHelpers: FieldArrayRenderProps) => {
    const showCsvButtons = props.values.assistantType === 'ai-ordering'
      || props.values.assistantType === 'dental-scheduling'
      || props.values.assistantType === 'ai-answering';
    return (
      <AddButtonWrapper>
        {/* Hidden file input for CSV import */}
        {showCsvButtons && (
          <input
            ref={csvImportInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={handleCSVImport}
          />
        )}
        {showCsvButtons && !isAssistantTypeDisabled && (
          <CsvTemplateButton
            themes={["secondary-small", "end-tight"]}
            text="Download Location Template"
            type="button"
            rightIcon={fileDownloadIcon}
            onClick={downloadLocationTemplate}
          />
        )}
        {showCsvButtons && (
          <CsvImportButton
            themes={["secondary-small", "end-tight"]}
            text="Import Locations"
            type="button"
            rightIcon={fileUploadIcon}
            disabled={isAssistantTypeDisabled}
            onClick={() => csvImportInputRef.current?.click()}
          />
        )}
        <Button
          themes={["flat"]}
          text="+ Add Location"
          type="button"
          className={addLocationName}
          onClick={() => handleAddLocationButtonClick(arrayHelpers)}
        />
      </AddButtonWrapper>
    );
  };

  const renderNoFormsMessage = () => {
    const assistantTypeLabel = assistantTypeOptions.find(option => option.value === props.values.assistantType)?.label || 'this assistant type';

    return (
      <div className={noFormsMessageWrapper}>
        <p className={noMessageStyle}>
          {search ?
            `No results found for "${search}".` :
            `No topics available for ${assistantTypeLabel}. This module may not be configured yet.`
          }
        </p>
        {!search && (
          <p className={xMessageStyle}>
            Please contact support if you expected to see topics here.
          </p>
        )}
      </div>
    );
  };

  const hasValidationErrors = Object.keys(props.errors).length > 0;

  // When initially configuring (no location or shared config apps yet), require PMS Name /
  // System Integration to be selected before subsequent fields become available.
  const isInitialConfiguration = !props.commonApp && (!props.locationApps || props.locationApps.length === 0);
  const initialPrerequisiteComplete =
    !isInitialConfiguration ||
    (props.values.assistantType === 'dental-scheduling' && !!props.values.pmsName) ||
    (props.values.assistantType === 'ai-ordering' && !!props.values.posNameOrdering) ||
    props.values.assistantType === 'ai-answering';

  return (
    <form>
      <AssistantTypeWrapper hasAssistantType={!!props.values.assistantType}>
        <DropdownField>
          <CustomDropdown
            label="Assistant Purpose"
            labelStyle={assistantTypeLabelStyle}
            handleOnChange={handleAssistantTypeChange}
            options={assistantTypeOptions}
            value={props.values.assistantType}
            placeholder="Select assistant type"
            size="medium"
            disabled={props.isLoadingTemplates || isAssistantTypeDisabled || submissionSuccess}
            tooltip={isAssistantTypeDisabled ? "Assistant types and PMS cannot be modified in this section. To make changes the user will need to speak to a member of the Voicify Engineering team who can make those changes in app.voicify." : undefined}
          />
          {props.errors.assistantType && isSubmitted && (
            <ValidationError>{props.errors.assistantType}</ValidationError>
          )}
        </DropdownField>

        {props.values.assistantType === 'dental-scheduling' && (
          <div ref={posNameRef}>
            <DropdownField>
              <CustomDropdown
                label="PMS Name"
                labelStyle={assistantTypeLabelStyle}
                handleOnChange={handlePmsNameChange}
                options={pmsNameOptions}
                value={props.values.pmsName}
                placeholder="Select PMS name"
                size="medium"
                disabled={props.isLoadingTemplates || isAssistantTypeDisabled || submissionSuccess}
                tooltip={isAssistantTypeDisabled ? "Assistant types and PMS cannot be modified in this section. To make changes the user will need to speak to a member of the Voicify Engineering team who can make those changes in app.voicify." : undefined}
              />
              {props.errors.pmsName && isSubmitted && (
                <ValidationError>{props.errors.pmsName}</ValidationError>
              )}
            </DropdownField>
          </div>
        )}

        {props.values.assistantType === 'ai-ordering' && (
          <div ref={posNameRef}>
            <DropdownField>
              <CustomDropdown
                label="System Integration"
                labelStyle={assistantTypeLabelStyle}
                handleOnChange={handlePosNameOrderingChange}
                options={posNameOrderingOptions}
                value={props.values.posNameOrdering}
                placeholder="Select POS name"
                size="medium"
                disabled={props.isLoadingTemplates || isAssistantTypeDisabled || submissionSuccess}
                tooltip={isAssistantTypeDisabled ? "Assistant types and PMS cannot be modified in this section. To make changes the user will need to speak to a member of the Voicify Engineering team who can make those changes in app.voicify." : undefined}
              />
              {props.errors.posNameOrdering && isSubmitted && (
                <ValidationError>{props.errors.posNameOrdering}</ValidationError>
              )}
            </DropdownField>
          </div>
        )}
      </AssistantTypeWrapper>

      {props.values.assistantType && (
        <>
          {!props.commonApp && (
            <SetupTypeWrapper>
              <DropdownField>
                <CustomDropdown
                  label="Environment"
                  labelStyle={assistantTypeLabelStyle}
                  handleOnChange={handleAssistantSetupTypeChange}
                  options={assistantSetupTypeOptions}
                  value={props.values.assistantSetupType}
                  placeholder="Select setup type"
                  size="medium"
                  disabled={props.isLoadingTemplates || submissionSuccess || !initialPrerequisiteComplete}
                />
                {props.errors.assistantSetupType && isSubmitted && (
                  <ValidationError>{props.errors.assistantSetupType}</ValidationError>
                )}
              </DropdownField>
            </SetupTypeWrapper>
          )}

          <CollapsingPanel
            isCollapsed={isLocationCollapsed}
            collapsedView={
              <FieldArray
                validateOnChange={false}
                name="locationNames"
                render={(arrayHelpers) => (
                  <LocationsPanelWrapper>
                    <LocationsHeader
                      onClick={() => setIsLocationCollapsed(!isLocationCollapsed)}
                    >
                      <LocationsHeaderLeft>
                        <ToggleIndicator direction="up" />
                        <LocationsHeaderIcon src={locationsIcon} />
                        <LocationsHeaderLabel>Deployment Locations</LocationsHeaderLabel>
                      </LocationsHeaderLeft>
                      <LocationsCount>{props.values.locationNames.filter(n => n.trim()).length} added</LocationsCount>
                    </LocationsHeader>
                  </LocationsPanelWrapper>
                )}
              />
            }
            expandedView={
              <FieldArray
                validateOnChange={false}
                name="locationNames"
                render={(arrayHelpers) => (
                  <LocationsPanelWrapper>
                    <LocationsHeader
                      onClick={() => setIsLocationCollapsed(!isLocationCollapsed)}
                    >
                      <LocationsHeaderLeft>
                        <ToggleIndicator direction="down" />
                        <LocationsHeaderIcon src={locationsIcon} />
                        <LocationsHeaderLabel>Deployment Locations</LocationsHeaderLabel>
                      </LocationsHeaderLeft>
                      <LocationsCount>{props.values.locationNames.filter(n => n.trim()).length} added</LocationsCount>
                    </LocationsHeader>

                    <LocationsList ref={locationNamesRef}>
                      {props.values.locationNames.map((locationName, index) => {
                        // Match existing apps by current name OR by renamed name (localAppNames lookup)
                        const existingApp = props.locationApps?.find((l) =>
                          l.name === locationName ||
                          localAppNames[l.id] === locationName
                        );
                        const isExisting = !!existingApp || (submissionSuccess && !!locationName.trim());
                        const needsStoreId = props.values.assistantType === 'ai-ordering' || props.values.assistantType === 'dental-scheduling';
                        const isNewOrg = !props.commonApp;
                        const locationNameEmpty = isSubmitted && !locationName.trim();
                        const rowError = locationNameEmpty
                          ? 'Location name is required'
                          : null;
                        const isEditingThisRow = isExisting && existingApp && editingAppId === existingApp.id;
                        return (
                          <LocationRowWrapper key={`locationNames.${index}`}>
                            <LocationRow>
                              <LocationRowContent>
                                <LocationRowInputs>
                                  {isEditingThisRow ? (
                                    <AppNameEditRow style={{ flex: 1 }}>
                                      <AppNameEditInput
                                        value={editingAppName}
                                        onChange={(e) => setEditingAppName(e.target.value)}
                                        onKeyDown={(e) => handleAppNameEditKeyDown(e, existingApp)}
                                        disabled={isSavingAppName}
                                        autoFocus
                                      />
                                      <AppNameEditAction
                                        src={checkIcon}
                                        onClick={() => handleSaveAppName(existingApp)}
                                        title="Save name"
                                      />
                                      <AppNameEditAction
                                        src={xIcon}
                                        onClick={handleCancelEditAppName}
                                        title="Cancel"
                                      />
                                    </AppNameEditRow>
                                  ) : (
                                    <>
                                      <LocationNameInput
                                        name={`locationNames.${index}`}
                                        disabled={isExisting}
                                        value={locationName}
                                        label=""
                                        placeholder="Location name"
                                        onChange={(e) => handleTextFieldChange(e, `locationNames.${index}`)}
                                        fieldContainerStyle={locationNameEmpty ? locationInputErrorContainerStyle : locationInputContainerStyle}
                                        className={locationInputStyle}
                                        autoFocus={index === props.values.locationNames.length - 1 && !isExisting}
                                        onKeyDown={(e) => handleAddLocationKeyDown(e)}
                                        useForm={false}
                                      />
                                      {isExisting && existingApp && (
                                        <AppNameEditIcon
                                          src={pencilIcon}
                                          onClick={() => handleStartEditAppName(existingApp.id, locationName)}
                                          title="Edit app name"
                                        />
                                      )}
                                    </>
                                  )}
                                  {!isEditingThisRow && props.values.assistantType === 'dental-scheduling' && (
                                    <TooltipWrapper
                                      text={props.commonApp ? "To change call direction after initial configuration, do so in app.voicify." : undefined}
                                      place="top"
                                      maxWidth={280}
                                    >
                                      <CallDirectionDropdown
                                        label=""
                                        handleOnChange={(_fieldName: string, val: string) => props.setFieldValue(`callDirections.${index}`, val)}
                                        options={props.commonApp ? subsequentVisitCallDirectionOptions : callDirectionOptions}
                                        value={props.values.callDirections?.[index] ?? ((existingApp?.featureFlags?.some((f: any) => f.id === OutboundCallFeatureFlagId) ?? props.commonApp?.featureFlags?.some(f => f.id === OutboundCallFeatureFlagId)) ? 'outbound' : 'inbound')}
                                        placeholder="Call Direction"
                                        size="medium"
                                        disabled={!!props.commonApp}
                                      />
                                    </TooltipWrapper>
                                  )}
                                  {!isEditingThisRow && needsStoreId && (
                                    <StoreIdField
                                      name={`storeIds.${index}`}
                                      disabled={isExisting}
                                      value={props.values.storeIds?.[index] ?? ''}
                                      label=""
                                      placeholder={isExisting ? '' : props.values.assistantType === 'dental-scheduling' ? 'Default Office ID' : 'Location ID'}
                                      onChange={(e) => props.setFieldValue(`storeIds.${index}`, e.target.value)}
                                      fieldContainerStyle={
                                        locationInputContainerStyle
                                      }
                                      className={locationInputStyle}
                                      useForm={false}
                                    />
                                  )}
                                </LocationRowInputs>
                                {isEditingThisRow && appNameSaveError && <AppNameError>{appNameSaveError}</AppNameError>}
                                {rowError && <ValidationError>{rowError}</ValidationError>}
                              </LocationRowContent>
                              {!isExisting && index !== 0 && (
                                <DeleteIcon
                                  src={deleteIcon}
                                  onClick={() => arrayHelpers.remove(index)}
                                />
                              )}
                            </LocationRow>
                          </LocationRowWrapper>
                        );
                      })}
                    </LocationsList>

                    {renderAddButton(arrayHelpers)}
                  </LocationsPanelWrapper>
                )}
              />
            }
          />

          <OutsideFormWrapper reserveMenuSpace={!props.commonApp && !props.values.assistantSetupType}>

            {(props.commonApp || props.values.assistantSetupType) && showCheckboxes && (
              <>
                <CheckBoxSectionsTitle ref={locationTopicsRef}>
                  Location-Specific Configuration
                </CheckBoxSectionsTitle>
                <CheckBoxSectionsDescription>
                  Select the form fields that will have different values per location.
                </CheckBoxSectionsDescription>

                {props.isLoadingTemplates || (filteredSections.length === 0 && !isSubmitted && !search) ? (
                  <LoaderWrapper>
                    <SecondaryLoader />
                  </LoaderWrapper>
                ) : (
                  <FieldArray
              name="selectedFormFields"
              render={() => {
                return (
                  <CheckboxFieldWrapper>
                    {!isSubmitted || hasValidationErrors ? (
                      Array.isArray(filteredSections) &&
                        filteredSections.length > 0 ? (
                        filteredSections.map((section, index) => {
                          if (activeIndex === index && activeIndex !== null) {
                            return (
                              <div key={index}>
                                <InsideFormWrapper>
                                  <SectionWrapper
                                    key={`${section.title}.${index}`}
                                  >
                                    <SectionTitle>
                                      <InsideFormHeader>
                                        {renderLocationButtons(index)}
                                      </InsideFormHeader>
                                    </SectionTitle>
                                    {renderSectionFormFields(section)}
                                    <PageNumberWrapper>
                                      <p>
                                        {activeIndex + 1} /{" "}
                                        {filteredSections.length}
                                      </p>
                                    </PageNumberWrapper>
                                  </SectionWrapper>
                                </InsideFormWrapper>
                                {renderSubmitButton()}
                              </div>
                            );
                          }
                          return null;
                        })
                      ) : (
                        <>
                          <InsideFormWrapper>
                            <SectionWrapper key={``}>
                              <SectionTitle>
                                <InsideFormHeader>
                                  {renderNoFormsMessage()}
                                  {renderLocationButtons(activeIndex)}
                                </InsideFormHeader>
                              </SectionTitle>
                            </SectionWrapper>
                          </InsideFormWrapper>
                          {renderSubmitButton()}
                        </>
                      )
                    ) : (
                      <ThankYouWrapper>
                        {submissionSuccess ? (
                          <>
                            <TextField
                              name="Submitted"
                              required={false}
                              value={props.status?.message ?? 'Your assistants have been successfully configured.'}
                              onChange={props.handleChange}
                              onBlur={props.handleBlur}
                              icon={checkIcon}
                              iconPosition="right"
                            />
                          </>
                        ) : (
                          <SubmissionProgressWrapper ref={stepperRef}>
                            <SubmissionProgressTitle>Setting up your assistants...</SubmissionProgressTitle>
                            <SubmissionStepList>
                              {SUBMISSION_PHASES.map((phase, idx) => {
                                const currentPhaseIndex = SUBMISSION_PHASES.findIndex(p => p.key === props.progressStep);
                                const isComplete = currentPhaseIndex > idx;
                                const isActive = currentPhaseIndex === idx;
                                const mode = props.progressMode ?? 'creating';
                                const showLocationRows = isActive && phase.key === 'create-locations';
                                const locationNames = props.values.locationNames.filter(n => n && n.trim());
                                const locationProgress = props.locationProgress ?? {};
                                const completedCount = Object.values(locationProgress).filter(v => v === 'complete').length;
                                const totalCount = locationNames.length;
                                const progressPercent = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
                                return (
                                  <React.Fragment key={phase.key}>
                                    <SubmissionStepItem isActive={isActive} isComplete={isComplete}>
                                      <SubmissionStepNumber isActive={isActive} isComplete={isComplete}>
                                        {isComplete ? '✓' : idx + 1}
                                      </SubmissionStepNumber>
                                      <SubmissionStepLabel isActive={isActive} isComplete={isComplete}>
                                        {phase.label(mode)}
                                      </SubmissionStepLabel>
                                      {isActive && <SubmissionStepSpinner><SecondaryLoader /></SubmissionStepSpinner>}
                                    </SubmissionStepItem>
                                    {showLocationRows && locationNames.length > 0 && (
                                      <>
                                        <LocationRowList>
                                          {locationNames.map(name => {
                                            const subStep = locationProgress[name] ?? 'pending';
                                            const rowStatus: 'pending' | 'active' | 'complete' =
                                              subStep === 'complete' ? 'complete' :
                                              subStep === 'pending' ? 'pending' : 'active';
                                            return (
                                              <LocationRowItem key={name} rowStatus={rowStatus}>
                                                <LocationRowIcon rowStatus={rowStatus}>
                                                  {rowStatus === 'complete' ? '✓' : rowStatus === 'active' ? '⟳' : '·'}
                                                </LocationRowIcon>
                                                <LocationRowName>{name}</LocationRowName>
                                                 <LocationRowSubStep>
                                                   {subStep === 'create-manage-menu'
                                                     ? (mode === 'updating' ? 'Updating Manage Menu app...' : 'Creating Manage Menu app...')
                                                     : (LOCATION_SUB_STEP_LABELS[subStep] ?? subStep)}
                                                 </LocationRowSubStep>
                                                {rowStatus === 'active' && <SubmissionStepSpinner><SecondaryLoader /></SubmissionStepSpinner>}
                                              </LocationRowItem>
                                            );
                                          })}
                                        </LocationRowList>
                                        {totalCount > 1 && (
                                          <ProgressBarWrapper>
                                            <ProgressBarLabel>{completedCount} of {totalCount} locations complete</ProgressBarLabel>
                                            <ProgressBarTrack>
                                              <ProgressBarFill percent={progressPercent} />
                                            </ProgressBarTrack>
                                          </ProgressBarWrapper>
                                        )}
                                      </>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </SubmissionStepList>
                          </SubmissionProgressWrapper>
                        )}
                        {renderSubmitButton()}
                      </ThankYouWrapper>
                    )}
                  </CheckboxFieldWrapper>
                );
              }}
            />
                )}
              </>
            )}          </OutsideFormWrapper>
        </>
      )}
    </form>
  );
};

const assistantTypeLabelStyle = css`
    font-family: Muli;
    font-size: 14px;
    color: ${dark_grey_blue};
    font-weight: 600;
    margin: 4px 0 4px 22px;
`;

const tooltipStyle = css`
  width: 20px;
  height: 20px;
  margin-top: 2px;
  margin-left: 8px;
  border: solid 1px ${text_blue};
  span {
    color: ${text_blue};
  }
`;

const tooltipContentStyle = css`
  .rc-tooltip-inner {
    font-family: Muli;
    font-size: 14px;
    font-weight: normal;
    font-style: normal;
    letter-spacing: 0.6px;
    z-index: 120;
    color: ${color_shades_darker};
    background-color: white;
    border: 1px solid ${color_shades_dark};
    padding: 8px;
    min-height: 0;
    box-shadow: -1px 4px 10px 0 rgba(48, 69, 98, 0.1);
    max-width: 200px;
    white-space: normal;
    word-wrap: break-word;
  }
`;

const separatorStyle = css`
  width: 1px;
  height: 30px;
  color: ${silver_two}
  ${breakpoint_small} {
    display: none;
  }
`;

const CheckBoxSectionsDescription = styled.p`
  font-family: Muli;
  font-size: 14px;
  color: ${color_shades_darker};
  margin: 0px 0px 16px 38px;
`;

const OutsideFormWrapper = styled.div<{ reserveMenuSpace?: boolean }>`
  display: flex;
  flex-direction: column;
  margin-top: 24px;
  padding-bottom: ${props => props.reserveMenuSpace ? '120px' : '24px'};
`;

const InsideFormHeader = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  padding: 0 0 20px 0;
  ${breakpoint_small} {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
    padding: 0 0 8px 0;
  }
`;

const SearchButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: auto;
  img {
    margin: 0px;
  }
  ${breakpoint_small} {
    width: 100%;
    margin-left: 0;
  }
`;

const SectionDropdownWrapper = styled.div`
  width: 280px;
  flex-shrink: 0;
  /* force StyledDropdown's wrapper and react-select to fill the container */
  > div,
  > div .Select {
    width: 100%;
  }
  /* hide the empty shell react-select renders — menu content is in the portal */
  .Select-menu-outer {
    display: none;
  }
  ${breakpoint_small} {
    width: 100%;
  }
`;

const SectionHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 12px;
  ${breakpoint_small} {
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
`;

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
`;

const OutsideFormHeader = css`
  margin-left: 8px;
`;

const SectionTitle = styled.div`
  font-family: Muli;
  font-size: 14px;
  margin-top: -4px;
  margin-bottom: 0px;
  width: 100%;
  box-sizing: border-box;
`;

const CheckBoxSectionsTitle = styled.div`
  font-family: Muli;
  font-size: 14px;
  font-weight: 600;
  margin: 0 32px 8px 38px;
`;

const CheckboxFieldWrapper = styled.div`
  padding: 0px 28px 0 28px;
  ${breakpoint_small} {
    padding: 8px;
  }
`;

const checkboxField = css`
  .check-label {
    font-weight: 500;
  }
  margin-left: 16px;
`;

const DeleteIcon = styled.img`
  cursor: pointer;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  margin-left: 12px;
  margin-top: 26px;
  opacity: 0.5;
  transition: opacity 0.15s;
  &:hover {
    opacity: 1;
  }
`;

const PageNumberWrapper = styled.div`
  display: flex;
  justify-content: right;
  font-size: 12px;
  color: ${silver_two};
  margin: -4px 16px 16px 0px;
`;

const LocationsPanelWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: right;
  gap: 4px;
  margin-top: 0px;
  margin-left: 8px;
  padding-bottom: 0px;
`;

const SubmitButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  margin: 24px 0 0 0;
`;

const InsideFormWrapper = styled.div`
  background: white;
  border: 1px solid ${color_shades_dark};
  box-sizing: border-box;
  border-radius: 8px;
  padding: 24px 12px 0px 16px;
  margin: 0px 0px 0px 0px;
`;

const ThankYouWrapper = styled.div`
  .form-submission-error {
    position: relative;
    right: auto;
    bottom: auto;
    width: 100%;
    margin-top: 16px;
    border-radius: 8px;
  }
`;

const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80px;
  margin: 16px 0;
`;

const lightButtonStyle = css`
  cursor: not-allowed;
`;

const addLocationName = css`
    color: ${text_blue};
    font-size: 14px;
    box-shadow: none;
    padding: 0 8px;
    margin: 0;
`;

const noFormsMessageWrapper = css`
  display: flex;
  flex-direction: column;
`;

const noMessageStyle = css`
  font-size: 14px;
  padding-top: 2px;
`;

const xMessageStyle = css`
  padding-top: 6px;
  color: ${color_shades_dark};
  font-style: italic;
`;

const AddButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  padding: 8px 21px 12px 21px;
  border-bottom: 1px solid ${color_shades_dark};
`;

// Button that triggers the CSV file input for bulk location import (AI Ordering)
const CsvImportButton = styled(Button)`
  font-size: 14px;
  margin: 0 8px 0 0;
  .right-img {
    margin-left: 8px;
    width: 16px;
  }
`;

// Button that downloads a blank location CSV template (AI Ordering)
const CsvTemplateButton = styled(Button)`
  font-size: 14px;
  margin: 0 8px 0 0;
  .right-img {
    margin-left: 8px;
    width: 16px;
  }
`;


const StyledButton = styled(Button)`
  margin: 0;
`;

// Store ID text field — rendered inline next to the location name for AI Ordering
const StoreIdField = styled(TextField)`
  max-width: 200px;
  margin-left: 8px;
`;

const CallDirectionDropdown = styled(StyledDropdown)`
  width: 200px;
  flex-shrink: 0;
  margin-left: 8px;
  margin-bottom: 0;
  padding: 0;

  /* Make the inner react-select control fill the full width */
  .Select,
  .Select-control {
    width: 100%;
  }

  /* Match TextField pale_grey disabled background */
  &.is-disabled .Select-control,
  .Select.is-disabled .Select-control {
    background: ${pale_grey};
    cursor: not-allowed;
  }
`;

const StyledSearchField = styled(SearchField)`
  && {
    padding: 0;
    margin-bottom: 0;
    flex: 1;
  }
  .field-container {
    height: 44px;
    border-radius: 8px;
    border: solid 1px ${color_shades_dark};
    box-shadow: 0px 1px 2px rgba(50, 70, 97, 0.1);
    margin-top: 0;
    img {
      margin-left: 8px;
    }
  }
  input {
    font-family: Muli, sans-serif;
    color: ${color_shades_darkest};
    font-size: 13px;
    font-weight: normal;
    letter-spacing: normal;
    border-radius: 8px;
    ::placeholder {
      font-family: Muli, sans-serif;
      font-size: 13px;
      font-weight: normal;
      font-style: normal;
      letter-spacing: normal;
      color: ${silver_two};
    }
  }
`;

const CustomDropdown = styled(StyledDropdown)`
    .Select-menu-outer {
        max-height: none !important;
        z-index: 10001;
        padding: 10px 0px;
    }
    
    .Select-menu {
        max-height: 300px;
    }
`;

const DropdownField = styled.div`
    display: flex;
    flex-direction: column;
    padding: 0 5px;
    margin-bottom: 24px;
`;

const AssistantTypeWrapper = styled.div<{ hasAssistantType: boolean }>`
    padding: 16px 16px 0 16px;
    ${props => !props.hasAssistantType ? 'height: calc(100vh - 325px);' : ''}
`;

const SetupTypeWrapper = styled.div`
    padding: 0 16px 0 16px;
`;

// ── Locations panel header row ──────────────────────────────────────────────
// Matches the Advanced Options collapsible style in App Info > Settings:
// full-width, top and bottom border only, no left/right border, no radius.

const LocationsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 24px 40px;
  background: white;
  border-top: 1px solid ${color_shades_dark};
  border-bottom: 1px solid ${color_shades_dark};
  cursor: pointer;
  user-select: none;
`;

const LocationsHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  .toggle-indicator {
    margin-top: -2px;
  }
`;

const LocationsHeaderLabel = styled.h4`
  && {
    font-family: Muli;
    font-style: normal;
    font-weight: normal;
    font-size: 18px;
    line-height: 1;
    color: ${color_shades_darkest};
    margin: 0 10px;
    align-self: center;
  }
`;

const LocationsHeaderIcon = styled.img`
  width: 24px;
  height: 24px;
  margin-left: 10px;
  flex-shrink: 0;
  align-self: center;
`;

// ── Location list and rows ──────────────────────────────────────────────────

const LocationsCount = styled.span`
  font-size: 12px;
  color: ${color_shades_darker};
  font-family: Muli;
`;

const LocationsList = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 21px;
  margin-top: 28px;
  max-height: 400px;
  overflow-y: auto;
`;

// Wraps a location row + its optional error message
const LocationRowWrapper = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${color_shades_light};
  &:last-of-type {
    border-bottom: none;
  }
`;

const LocationRow = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 10px 0;
  background: white;
  transition: background 0.1s;
  &:hover {
    background: ${color_shades_lightest};
  }
`;

const ValidationErrorStyle = styled.div`
  display: flex;
  align-items: center;
  font-family: Muli, "sans-serif";
  font-weight: normal;
  font-size: 14px;
  color: ${color_shades_darkest};
  background-color: ${color_variants_decline_light_1};
  border: 1px solid ${color_colors_decline};
  border-radius: 8px;
  padding: 8px 16px;
  margin: 8px 0;
`;

const ValidationErrorIcon = styled.img`
  margin-right: 8px;
  flex-shrink: 0;
`;

const ValidationError: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <ValidationErrorStyle data-validation-error>
    <ValidationErrorIcon src={alertCircleIcon} />
    {children}
  </ValidationErrorStyle>
);

const LocationRowContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const LocationRowInputs = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const LocationNameInput = styled(TextField)`
  flex: 1;
  min-width: 0;
  margin-bottom: 0;
`;

// input container for location rows — matches dropdown border/radius/height
const locationInputContainerStyle = css`
  border: 1px solid ${color_shades_dark} !important;
  box-shadow: none !important;
  background: white !important;
  height: 48px !important;
  min-height: 48px !important;
  border-radius: 8px !important;
  margin-top: 0 !important;
`;

const locationInputErrorContainerStyle = css`
  border: 1px solid #d0021b !important;
  box-shadow: none !important;
  background: white !important;
  height: 48px !important;
  min-height: 48px !important;
  border-radius: 8px !important;
  margin-top: 0 !important;
`;

const locationInputStyle = css`
  margin-bottom: 0 !important;
`;

export const SUBMISSION_PHASES: { key: string; label: (mode: 'creating' | 'updating') => string }[] = [
    { key: 'setup-module',       label: (m) => m === 'updating' ? 'Updating shared configuration...' : 'Creating shared configuration...' },
    { key: 'configure-template', label: (m) => m === 'updating' ? 'Updating template configuration...' : 'Configuring template...' },
    { key: 'create-locations',   label: (m) => m === 'updating' ? 'Updating location apps...' : 'Creating location apps...' },
    { key: 'publishing',         label: () => 'Publishing...' },
];

export const LOCATION_SUB_STEP_LABELS: Record<string, string> = {
    'pending':            'Pending',
    'creating-app':       'Creating app...',
    'updating-app':       'Updating app...',
    'applying-template':  'Applying template...',
    'create-manage-menu': 'Creating Manage Menu app...',
    'pull-menu':          'Pulling menu from POS...',
    'sync-module':        'Syncing to AI Ordering Module...',
    'complete':           'Complete',
};

const SubmissionProgressWrapper = styled.div`
    display: flex;
    flex-direction: column;
    padding: 24px 32px 24px 38px;
    gap: 0;
`;

const SubmissionProgressTitle = styled.p`
    font-family: Muli, "sans-serif";
    font-size: 14px;
    font-weight: 700;
    color: ${dark_grey_blue};
    margin: 0 0 20px 0;
`;

const SubmissionStepList = styled.ol`
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0;
`;

interface StepStyleProps {
    isActive: boolean;
    isComplete: boolean;
}

const SubmissionStepItem = styled.li<StepStyleProps>`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid ${color_shades_light};
    opacity: ${({ isActive, isComplete }) => (isActive || isComplete ? 1 : 0.4)};
    &:last-of-type {
        border-bottom: none;
    }
`;

const SubmissionStepNumber = styled.span<StepStyleProps>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    min-width: 28px;
    border-radius: 50%;
    font-family: Muli, "sans-serif";
    font-size: 13px;
    font-weight: 700;
    background: ${({ isComplete, isActive }) =>
        isComplete ? text_blue : isActive ? text_blue : color_shades_light};
    color: ${({ isComplete, isActive }) =>
        isComplete || isActive ? 'white' : color_shades_darker};
    transition: background 0.2s, color 0.2s;
`;

const SubmissionStepLabel = styled.span<StepStyleProps>`
    font-family: Muli, "sans-serif";
    font-size: 14px;
    font-weight: ${({ isActive }) => (isActive ? 700 : 400)};
    color: ${({ isActive, isComplete }) =>
        isActive ? dark_grey_blue : isComplete ? text_blue : color_shades_darker};
    flex: 1;
    transition: color 0.2s;
`;

const SubmissionStepSpinner = styled.span`
    display: flex;
    align-items: center;
    margin-left: 4px;
`;

const LocationRowList = styled.ul`
    list-style: none;
    margin: 4px 0 12px 40px;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0;
`;

interface LocationRowProps {
    rowStatus: 'pending' | 'active' | 'complete';
}

const LocationRowItem = styled.li<LocationRowProps>`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 6px 0;
    opacity: ${({ rowStatus }) => (rowStatus === 'pending' ? 0.4 : 1)};
    transition: opacity 0.2s;
`;

const LocationRowIcon = styled.span<LocationRowProps>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    min-width: 18px;
    border-radius: 50%;
    font-size: 11px;
    font-weight: 700;
    background: ${({ rowStatus }) =>
        rowStatus === 'complete' ? text_blue :
        rowStatus === 'active' ? text_blue :
        color_shades_light};
    color: ${({ rowStatus }) =>
        rowStatus === 'pending' ? color_shades_darker : 'white'};
    transition: background 0.2s;
`;

const LocationRowName = styled.span`
    font-family: Muli, "sans-serif";
    font-size: 13px;
    font-weight: 600;
    color: ${dark_grey_blue};
    min-width: 120px;
`;

const LocationRowSubStep = styled.span`
    font-family: Muli, "sans-serif";
    font-size: 12px;
    color: ${color_shades_darker};
    font-style: italic;
`;

const ProgressBarWrapper = styled.div`
    margin: 8px 0 4px 0;
`;

const ProgressBarLabel = styled.p`
    font-family: Muli, "sans-serif";
    font-size: 12px;
    color: ${color_shades_darker};
    margin: 0 0 6px 0;
`;

const ProgressBarTrack = styled.div`
    background: ${color_shades_light};
    border-radius: 4px;
    height: 6px;
    width: 100%;
    overflow: hidden;
`;

interface ProgressBarFillProps {
    percent: number;
}

const ProgressBarFill = styled.div<ProgressBarFillProps>`
    background: ${text_blue};
    height: 100%;
    width: ${({ percent }) => percent}%;
    border-radius: 4px;
    transition: width 0.3s ease;
`;

// ── App Name inline edit components ─────────────────────────────────────────


const AppNameDisplayRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const AppNameText = styled.span`
  font-family: Muli, sans-serif;
  font-size: 14px;
  color: ${color_shades_darkest};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AppNameEditIcon = styled.img`
  cursor: pointer;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  opacity: 0.5;
  transition: opacity 0.15s;
  &:hover {
    opacity: 1;
  }
`;

const AppNameEditRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const AppNameEditInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 38px;
  padding: 0 10px;
  font-family: Muli, sans-serif;
  font-size: 14px;
  color: ${color_shades_darkest};
  border: 1px solid ${color_shades_dark};
  border-radius: 8px;
  outline: none;
  box-sizing: border-box;
  &:focus {
    border-color: ${text_blue};
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AppNameEditAction = styled.img`
  cursor: pointer;
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  opacity: 0.7;
  transition: opacity 0.15s;
  &:hover {
    opacity: 1;
  }
`;

const AppNameError = styled.span`
  font-family: Muli, sans-serif;
  font-size: 12px;
  color: ${color_colors_decline};
  margin-left: 4px;
  white-space: nowrap;
`;

export default SparkLocationsForm;
